import cls from "@fvilers/cls";
import { ComponentPropsWithRef, ElementType, useEffect, useRef } from "react";
import styles from "./ScrollShadow.module.css";

type Props<T extends ElementType = "div"> = {
  as?: T;
  orientation?: "auto" | "horizontal" | "vertical";
  shadowSize?: number;
} & ComponentPropsWithRef<T>;

export function ScrollShadow<T extends ElementType = "div">({
  as,
  children,
  className,
  orientation = "auto",
  ref: propsRef,
  shadowSize = 50,
  style,
  ...props
}: Props<T>) {
  const ref = useRef<T extends ElementType<infer E> ? E : HTMLElement>(null);
  const refToUse = propsRef ?? ref;
  const Component = as || "div";

  useEffect(() => {
    const element = refToUse.current;
    if (!element) return;

    function handleScroll() {
      if (!element) return;
      const { clientHeight, scrollHeight, scrollTop } = element;
      const bottomSize =
        orientation === "horizontal"
          ? 0
          : Math.min(scrollHeight - clientHeight - scrollTop, shadowSize);
      const topSize =
        orientation === "horizontal" ? 0 : Math.min(scrollTop, shadowSize);
      const rightSize =
        orientation === "vertical"
          ? 0
          : Math.min(
              element.scrollWidth - element.clientWidth - element.scrollLeft,
              shadowSize,
            );
      const leftSize =
        orientation === "vertical"
          ? 0
          : Math.min(element.scrollLeft, shadowSize);

      element.style.setProperty("--shadow-left", `${leftSize}px`);
      element.style.setProperty("--shadow-right", `${rightSize}px`);
      element.style.setProperty("--shadow-top", `${topSize}px`);
      element.style.setProperty("--shadow-bottom", `${bottomSize}px`);
    }

    handleScroll();
    element.addEventListener("scroll", handleScroll);
    const mutationObserver = new MutationObserver(handleScroll);
    mutationObserver.observe(element, { childList: true });
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [orientation, refToUse, shadowSize]);

  return (
    <Component
      className={cls(styles.ScrollShadowArea, className)}
      ref={refToUse}
      style={
        {
          ...style,
          "--scroll-shadow-size": `${shadowSize}px`,
        } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </Component>
  );
}
